import clsx from 'clsx'
import { isWeekend, isWithinInterval } from 'date-fns'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import {
  ApiDepositInstruction,
  ApiWithdrawalInstruction,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery
} from '@/api/rtk-api'
import {
  Card,
  DateRangePicker,
  last7Days,
  last30Days,
  last90Days,
  lastYear,
  Rule,
  Statistic,
  UnlabelledLineChart
} from '@/components/core-v2'
import { TENANT } from '@/const'
import { Currency, formatCurrency } from '@/utilities/currency'
import { displayPercentage } from '@/utilities/number'

import { TradePerformanceChart } from '../../components/trade-performance-chart'
import { TradeTemplate } from '../../templates/trade-template'
import classes from './index.module.scss'
import { Loading } from './loading'

export const ReportingTradePerformancePage = () => {
  const form = useForm()
  const { source, tradeId } = useParams()
  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
    tenant: TENANT,
    accountIdentifier: tradeId,
    accountSource: source
  })

  if (isLoading) return <Loading />

  const { accountSnapShots } = data

  const [firstSnapshot] = accountSnapShots
  const lastSnapshot = accountSnapShots.at(-1)

  const defaultDateRange: [Date, Date] = [
    new Date(firstSnapshot.valuationDate),
    new Date(lastSnapshot.valuationDate)
  ]

  const dateRangeValue: [Date, Date] = form.watch('performance-date-range', defaultDateRange)

  const snapshotsInRange = accountSnapShots.filter(snapshot =>
    isWithinInterval(new Date(snapshot.valuationDate), {
      start: new Date(dateRangeValue[0]),
      end: new Date(dateRangeValue[1])
    })
  )

  const lastSnapshotInRange = snapshotsInRange.at(-1)
  const currency = lastSnapshotInRange.notionalCurrency as Currency
  const { protectedAmount } = lastSnapshotInRange
  const { pendingPerformanceAllocation, performanceAllocation } = lastSnapshotInRange
  const totalPerformanceAllocation =
    (performanceAllocation || 0) + (pendingPerformanceAllocation || 0)
  const { deposits, miniAccountValueChartData, withdrawals } = snapshotsInRange.reduce<{
    deposits: number
    miniAccountValueChartData: [number, number][]
    withdrawals: number
  }>(
    (acc, snapshot) => {
      acc.miniAccountValueChartData.push([
        new Date(snapshot.valuationDate).getTime(),
        snapshot.accountValue
      ])

      const { deposits, withdrawals } = snapshot.operations.reduce(
        (acc, op) => {
          const withdrawals =
            op.type === 'Withdrawal' && op.state === 'Completed'
              ? (op.associatedInstruction as ApiWithdrawalInstruction).requestedValue
              : 0

          const deposits =
            op.type === 'Deposit' && op.state === 'Completed'
              ? (op.associatedInstruction as ApiDepositInstruction).requestedValue
              : 0

          acc.withdrawals += withdrawals
          acc.deposits += deposits

          return acc
        },
        { withdrawals: 0, deposits: 0 }
      )

      acc.withdrawals += withdrawals
      acc.deposits += deposits
      return acc
    },
    { miniAccountValueChartData: [], withdrawals: 0, deposits: 0 }
  )
  const accountPerformance = Number(
    ((snapshotsInRange.reduce((acc, r) => acc * r.growthFactor, 1) - 1) * 100).toFixed(2)
  )
  const accountPerformanceString = `${accountPerformance > 0 ? '+' : ''}${accountPerformance}%`
  const netContributions = formatCurrency(deposits - withdrawals, currency)

  const snapshotCount = snapshotsInRange.length
  const returns = snapshotsInRange.map(s => s.growthFactor - 1)
  const mean = returns.reduce((acc, r) => r, 0) / snapshotCount
  const variance = returns.map(r => (r - mean) ** 2).reduce((acc, x) => acc + x, 0)
  const realisedVolatility = displayPercentage(
    Math.sqrt(variance / snapshotCount) * Math.sqrt(252),
    2
  )

  const cumulativeGrowthFactors = returns.map((r, index) =>
    snapshotsInRange
      .map(snapshot => snapshot.growthFactor)
      .slice(0, index + 1)
      .reduce((acc, r) => acc * r, 1)
  )
  const maxDrawdown = cumulativeGrowthFactors
    .map((growthFactor, index) => {
      const peak = cumulativeGrowthFactors
        .slice(0, index + 1)
        .reduce((max, d) => Math.max(max, d), growthFactor)

      return [peak - growthFactor, peak]
    })
    .reduce((max, d) => (d[0] >= max[0] ? d : max))
  const maxDrawdownPercent = displayPercentage(
    maxDrawdown[1] !== 0 ? maxDrawdown[0] / maxDrawdown[1] : 0,
    2
  )

  return (
    <TradeTemplate tabValue="performance">
      <div className="d-flex justify-content-space-between">
        <FormProvider {...form}>
          <DateRangePicker
            isPopover
            showClearDatesButton
            defaultValue={defaultDateRange}
            id="performance-date-range"
            mapDays={({ date }) => ({
              disabled: isWeekend(new Date(date as any))
            })}
            speedDials={[last7Days, last30Days, last90Days, lastYear]}
          />
        </FormProvider>
      </div>
      <div className={clsx(classes.statisticsWrapper, 'mt-8')}>
        <Card.Wrapper className={classes.cardWrapper} spacing="sm">
          <div className={classes.accountValueWrapper}>
            <Statistic.Wrapper>
              <Statistic.Header>Total account value</Statistic.Header>
              <Statistic.Stat>
                {formatCurrency(lastSnapshotInRange.accountValue, currency)}
              </Statistic.Stat>
            </Statistic.Wrapper>
            <UnlabelledLineChart
              isPositive
              data={miniAccountValueChartData}
              height={50}
              width={null}
            />
          </div>
          <Rule className="mt-8 mb-5" />
          <div className={classes.relativeStatWrapper}>
            <Statistic.RelativeStat statisticNumber={accountPerformance}>
              {accountPerformanceString}
            </Statistic.RelativeStat>
          </div>
        </Card.Wrapper>
        <Card.Wrapper className={classes.cardWrapper} spacing="sm">
          <div className={classes.depositsWrapper}>
            <Statistic.Wrapper>
              <Statistic.Header>Deposits</Statistic.Header>
              <Statistic.Stat>{formatCurrency(deposits, currency)}</Statistic.Stat>
            </Statistic.Wrapper>
            <Statistic.Wrapper>
              <Statistic.Header>Withdrawals</Statistic.Header>
              <Statistic.Stat>{formatCurrency(withdrawals, currency)}</Statistic.Stat>
            </Statistic.Wrapper>
          </div>
          <Rule className="mt-10 mb-5" />
          <div className={classes.relativeStatWrapper}>
            <Statistic.RelativeStat statisticNumber={deposits - withdrawals}>
              {netContributions}
            </Statistic.RelativeStat>
            <Statistic.RelativeLabel>Net Contributions</Statistic.RelativeLabel>
          </div>
        </Card.Wrapper>
        <Card.Wrapper spacing="sm">
          <Statistic.Wrapper>
            <Statistic.Header>Protection Amount</Statistic.Header>
            <Statistic.Stat>{formatCurrency(protectedAmount, currency)}</Statistic.Stat>
          </Statistic.Wrapper>
          <Rule className="my-6" />
          <Statistic.Wrapper>
            <Statistic.Header>Current Allocation</Statistic.Header>
            <Statistic.Stat>{formatCurrency(totalPerformanceAllocation, currency)}</Statistic.Stat>
          </Statistic.Wrapper>
        </Card.Wrapper>
        <Card.Wrapper spacing="sm">
          <Statistic.Wrapper>
            <Statistic.Header>Maximum Drawdown</Statistic.Header>
            <Statistic.Stat>{maxDrawdownPercent}</Statistic.Stat>
          </Statistic.Wrapper>
          <Rule className="my-6" />
          <Statistic.Wrapper>
            <Statistic.Header>Realised Volatility</Statistic.Header>
            <Statistic.Stat>{realisedVolatility}</Statistic.Stat>
          </Statistic.Wrapper>
        </Card.Wrapper>
      </div>
      <div className="mt-8">
        <TradePerformanceChart dateRange={dateRangeValue} />
      </div>
    </TradeTemplate>
  )
}
